import React, { ChangeEvent, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  ErrorMessage,
  Field,
  Form,
  Formik,
  FormikErrors,
  FormikProps,
} from 'formik';
import * as Styled from './LoginPage.styles';
import * as actions from './actions';
import { selectors } from './reducer';
import {
  formatPhoneNumberToBeValid,
  setToLocalStorage,
} from '../../global/helpers';
import CustomCheckbox from '../../components/CustomCheckbox';
import CodeConfirmation from './CodeConfirmation';
import { INITIAL_AUTHORIZATION_MODAL_FORM_STATE } from './constants';
import Loader from '../../components/Loader';

interface FormValues {
  phone: string;
  agreementAccepted: boolean;
  method: 'sms' | 'whatsApp';
}

const LoginPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const loading = useSelector(selectors.loading);
  const whatsAppAuthNumber = useSelector(selectors.whatsAppAuthNumber);

  const [isConfirmationCodeSent, setIsConfirmationCodeSent] =
    useState<boolean>(false);
  // eslint-disable-next-line
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [authMethod, setAuthMethod] = useState<'sms' | 'whatsApp'>('sms');
  const wasSubmitted = useSelector(selectors.wasSubmitted);
  const errorMessage = useSelector(selectors.errorMessage);
  const error = useSelector(selectors.error);

  const handlePhoneChange = (
    props: FormikProps<any>,
    event: ChangeEvent<HTMLInputElement>,
    inputName: string
  ) => {
    // @ts-ignore
    const phoneInputValue = event.target.value.replaceAll(/[\s-()]/g, '');
    if (!phoneInputValue.match(/^\+7\d{0,10}$/)) {
      return;
    }
    props.setErrors({
      ...props.errors,
      [inputName]: '',
    });
    props.setFieldValue(inputName, phoneInputValue);
    setPhoneNumber(phoneInputValue);
    dispatch(actions.clearErrorMessage());
  };

  const handleAgreementCheckboxChange = (
    props: FormikProps<any>,
    isChecked: boolean
  ) => {
    props.setFieldValue('agreementAccepted', isChecked);
    props.setErrors({
      ...props.errors,
      agreementAccepted: '',
    });
  };

  const validateForm = (values: {
    phone: string;
    agreementAccepted: boolean;
  }) => {
    const { phone } = values;
    const errors: FormikErrors<FormValues> = {};

    if (!phone) {
      errors.phone = t('LoginPage.PHONE_EMPTY_ERROR');
    }
    if (!formatPhoneNumberToBeValid(values.phone)) {
      errors.phone = t('LoginPage.PHONE_INVALID_ERROR');
    }
    if (!values.agreementAccepted) {
      errors.agreementAccepted = t('LoginPage.ERROR_CHECKBOX');
    }
    return errors;
  };

  const sendMessageHandler = () => {
    const message = encodeURIComponent(
      String(t('CodeConfirmation.WHATSAPP_CODE'))
    );
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${whatsAppAuthNumber}&text=${message}`;

    window.open(whatsappUrl, '_blank');
  };

  const handleSubmitPhoneForm = async (values: {
    phone: string;
    agreementAccepted: boolean;
    method: string;
  }) => {
    const typedMethod = values.method as 'sms' | 'whatsApp';
    setPhoneNumber(values.phone);
    setAuthMethod(typedMethod);
    dispatch(
      actions.getValidationCode.request({
        phone: values.phone,
        method: typedMethod,
      })
    );
  };
  useEffect(() => {
    if (!wasSubmitted) return;

    const success = !loading && !error && !errorMessage && phoneNumber;

    if (success) {
      setIsConfirmationCodeSent(true);
      if (authMethod === 'whatsApp') {
        sendMessageHandler();
      }
    }
  }, [wasSubmitted, loading, error, errorMessage, phoneNumber, authMethod]);

  const pageContent = !isConfirmationCodeSent ? (
    <Styled.LoginPagePhoneConfirmation>
      <div className="title">{t('LoginPage.TITLE')}</div>
      <Formik
        initialValues={INITIAL_AUTHORIZATION_MODAL_FORM_STATE}
        onSubmit={handleSubmitPhoneForm}
        validate={validateForm}
      >
        {(props: FormikProps<any>) => (
          <Form className="form-container">
            <div className="form-block">
              <label className="form-label" htmlFor="phone">
                {t('LoginPage.PHONE_LABEL')}
              </label>
              <Field
                id="phone"
                name="phone"
                type="phone"
                className={`input ${props.errors.phone && 'input-error'}`}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handlePhoneChange(props, e, 'phone')
                }
              />
              <ErrorMessage
                name="phone"
                render={(msg: string) => (
                  <div className="error-message-label">{msg}</div>
                )}
              />

              {!loading && errorMessage && (
                <div className="error-message-label">
                  {t('LoginPage.NOT_ALLOWES_FOR_ROLE')}
                </div>
              )}
            </div>

            <Styled.LoginAgreementContainer>
              <div className="checkbox-container">
                <div className="checkbox">
                  <CustomCheckbox
                    id="agreement"
                    onChange={(e) => handleAgreementCheckboxChange(props, e)}
                    checked={props.values.agreementAccepted}
                    errorChecked={Boolean(props.errors.agreementAccepted)}
                  />
                </div>
                <span className="accept">
                  {t('LoginPage.ACCEPT')}
                  <a
                    href="/user-agreement.html"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>{t('LoginPage.USER_AGREEMENT')}</span>
                  </a>{' '}
                  и
                  <a
                    href="/confidentiality.html"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>{t('LoginPage.USER_CONFIDENTIALITY')}</span>
                  </a>
                </span>
              </div>
              {Boolean(props.errors.agreementAccepted) && (
                <div className="checkbox-error-message">
                  {t('LoginPage.ERROR_CHECKBOX')}
                </div>
              )}
            </Styled.LoginAgreementContainer>

            <Styled.LoginPageButtonsContainer>
              {whatsAppAuthNumber !== '77777777777' && (
                <Styled.FormSubmitButton
                  type="submit"
                  style={
                    props.values.agreementAccepted && !props.errors.phone
                      ? {}
                      : { opacity: 0.5 }
                  }
                  onClick={() => {
                    props.setFieldValue('method', 'whatsApp');
                  }}
                >
                  {t('LoginPage.AUTH_WHATSAPP')}
                </Styled.FormSubmitButton>
              )}

              <Styled.FormSubmitButton
                type="submit"
                style={
                  props.values.agreementAccepted && !props.errors.phone
                    ? {}
                    : { opacity: 0.5 }
                }
                onClick={() => {
                  props.setFieldValue('method', 'sms');
                }}
              >
                {t('LoginPage.AUTH_SMS')}
              </Styled.FormSubmitButton>
            </Styled.LoginPageButtonsContainer>
          </Form>
        )}
      </Formik>
    </Styled.LoginPagePhoneConfirmation>
  ) : (
    <CodeConfirmation
      phoneNumber={phoneNumber}
      whatsappMethod={authMethod === 'whatsApp'}
      whatsAppAuthNumber={whatsAppAuthNumber}
      sendMessageHandler={sendMessageHandler}
    />
  );

  useEffect(() => {
    dispatch(actions.getWhatsappAuthNumber.request());

    if (history.location.search.includes('token')) {
      const token = history.location.search.split('=')[1];
      setToLocalStorage('accessToken', token);
      setToLocalStorage('refreshToken', token);
      dispatch(actions.getUserInfo.request({ token }));
      setTimeout(() => {
        // history.push('/orders');
        window.location.href = '/orders';
      }, 1000);
    }
  }, []);
  return (
    <>
      <Styled.LoginPageContainer>
        <Styled.LoginPageContent>{pageContent}</Styled.LoginPageContent>
      </Styled.LoginPageContainer>
      {loading && <Loader />}
    </>
  );
};

export default LoginPage;
